import { Circle, Loader2, CircleDot, CheckCircle, XCircle, MinusCircle } from 'lucide-react';
import clsx from 'clsx';

export type ExtractionStatus = 'pending' | 'processing' | 'staged' | 'completed' | 'failed' | 'skipped' | null;

interface ExtractionStatusIconProps {
  status: ExtractionStatus;
  size?: 'sm' | 'md' | 'lg';
  showLabel?: boolean;
  onClick?: () => void;
}

const statusConfig: Record<NonNullable<ExtractionStatus>, {
  icon: typeof Circle;
  color: string;
  bgColor: string;
  spin: boolean;
  label: string;
  tooltip: string;
}> = {
  pending: {
    icon: Circle,
    color: 'text-gray-400',
    bgColor: 'bg-gray-100',
    spin: false,
    label: 'Pending',
    tooltip: 'Awaiting extraction',
  },
  processing: {
    icon: Loader2,
    color: 'text-blue-500',
    bgColor: 'bg-blue-100',
    spin: true,
    label: 'Processing',
    tooltip: 'Extracting data...',
  },
  staged: {
    icon: CircleDot,
    color: 'text-amber-500',
    bgColor: 'bg-amber-100',
    spin: false,
    label: 'Review',
    tooltip: 'Ready for review',
  },
  completed: {
    icon: CheckCircle,
    color: 'text-green-500',
    bgColor: 'bg-green-100',
    spin: false,
    label: 'Complete',
    tooltip: 'Extraction complete',
  },
  failed: {
    icon: XCircle,
    color: 'text-red-500',
    bgColor: 'bg-red-100',
    spin: false,
    label: 'Failed',
    tooltip: 'Extraction failed - click to retry',
  },
  skipped: {
    icon: MinusCircle,
    color: 'text-gray-300',
    bgColor: 'bg-gray-50',
    spin: false,
    label: 'Skipped',
    tooltip: 'Not extractable',
  },
};

const sizeConfig = {
  sm: {
    icon: 'w-3.5 h-3.5',
    badge: 'px-1.5 py-0.5 text-[10px]',
    gap: 'gap-1',
  },
  md: {
    icon: 'w-4 h-4',
    badge: 'px-2 py-0.5 text-xs',
    gap: 'gap-1.5',
  },
  lg: {
    icon: 'w-5 h-5',
    badge: 'px-2.5 py-1 text-sm',
    gap: 'gap-2',
  },
};

export function ExtractionStatusIcon({
  status,
  size = 'md',
  showLabel = false,
  onClick,
}: ExtractionStatusIconProps) {
  if (!status) return null;

  const config = statusConfig[status];
  if (!config) return null;

  const sizes = sizeConfig[size];
  const Icon = config.icon;

  const isClickable = onClick && (status === 'staged' || status === 'failed');

  return (
    <div
      className={clsx(
        'inline-flex items-center',
        sizes.gap,
        isClickable && 'cursor-pointer hover:opacity-80 transition-opacity'
      )}
      onClick={isClickable ? onClick : undefined}
      title={config.tooltip}
    >
      <Icon
        className={clsx(
          sizes.icon,
          config.color,
          config.spin && 'animate-spin'
        )}
      />
      {showLabel && (
        <span
          className={clsx(
            'font-medium rounded-full',
            sizes.badge,
            config.color,
            config.bgColor
          )}
        >
          {config.label}
        </span>
      )}
    </div>
  );
}

/**
 * Badge variant for use inline with document names
 */
interface ExtractionBadgeProps {
  status: ExtractionStatus;
  onReview?: () => void;
  onRetry?: () => void;
}

export function ExtractionBadge({ status, onReview, onRetry }: ExtractionBadgeProps) {
  if (!status) return null;

  const config = statusConfig[status];
  if (!config) return null;

  const Icon = config.icon;

  // Show clickable badge for staged and failed
  if (status === 'staged' && onReview) {
    return (
      <button
        onClick={(e) => {
          e.stopPropagation();
          onReview();
        }}
        className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium text-amber-700 bg-amber-100 hover:bg-amber-200 rounded-full transition-colors"
        title={config.tooltip}
      >
        <Icon className="w-3 h-3" />
        Review
      </button>
    );
  }

  if (status === 'failed' && onRetry) {
    return (
      <button
        onClick={(e) => {
          e.stopPropagation();
          onRetry();
        }}
        className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium text-red-700 bg-red-100 hover:bg-red-200 rounded-full transition-colors"
        title={config.tooltip}
      >
        <Icon className="w-3 h-3" />
        Retry
      </button>
    );
  }

  // For other statuses, just show the icon
  return (
    <span
      className={clsx(
        'inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-medium rounded-full',
        config.color,
        config.bgColor
      )}
      title={config.tooltip}
    >
      <Icon className={clsx('w-3 h-3', config.spin && 'animate-spin')} />
    </span>
  );
}

/**
 * Get just the color class for a status
 */
export function getExtractionStatusColor(status: ExtractionStatus): string {
  if (!status) return 'text-gray-400';
  return statusConfig[status]?.color || 'text-gray-400';
}

/**
 * Get the tooltip text for a status
 */
export function getExtractionStatusTooltip(status: ExtractionStatus): string {
  if (!status) return '';
  return statusConfig[status]?.tooltip || '';
}
